import React, { useEffect, useState } from 'react';
import { XIcon, SaveIcon, PlusIcon, TrashIcon, MailIcon, PhoneIcon, MapPinIcon, CheckIcon } from 'lucide-react';
// Types
export type EmailType = 'Work' | 'Personal' | 'Other';
export type PhoneType = 'Mobile' | 'Work' | 'Home' | 'Fax';
export type AddressType = 'Billing' | 'Shipping' | 'Physical' | 'Mailing';
export interface ContactEmail {
  id: string;
  type: EmailType;
  value: string;
  isPrimary: boolean;
}
export interface ContactPhone {
  id: string;
  type: PhoneType;
  value: string;
  isPrimary: boolean;
}
export interface ContactAddress {
  id: string;
  type: AddressType;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isPrimary: boolean;
}
export interface ContactData {
  id: string;
  name: string;
  role: string;
  emails: ContactEmail[];
  phones: ContactPhone[];
  addresses: ContactAddress[];
  // Legacy fields for backward compatibility
  email?: string;
  phone?: string;
  isPrimary?: boolean; // Contact-level primary flag
}
interface ContactFormProps {
  contact?: ContactData;
  onSave: (contactData: ContactData) => void;
  onCancel: () => void;
  locale: string;
}
export function ContactForm({
  contact,
  onSave,
  onCancel,
  locale
}: ContactFormProps) {
  // Initialize state with migration logic for legacy data
  const [formData, setFormData] = useState<ContactData>(() => {
    const baseData = {
      id: contact?.id || '',
      name: contact?.name || '',
      role: contact?.role || '',
      isPrimary: contact?.isPrimary || false,
      emails: contact?.emails || [],
      phones: contact?.phones || [],
      addresses: contact?.addresses || []
    };
    // Migrate legacy email if no emails array exists
    if (baseData.emails.length === 0 && contact?.email) {
      baseData.emails.push({
        id: `email-${Date.now()}`,
        type: 'Work',
        value: contact.email,
        isPrimary: true
      });
    } else if (baseData.emails.length === 0) {
      // Add empty initial email
      baseData.emails.push({
        id: `email-${Date.now()}`,
        type: 'Work',
        value: '',
        isPrimary: true
      });
    }
    // Migrate legacy phone if no phones array exists
    if (baseData.phones.length === 0 && contact?.phone) {
      baseData.phones.push({
        id: `phone-${Date.now()}`,
        type: 'Work',
        value: contact.phone,
        isPrimary: true
      });
    }
    return baseData;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'details' | 'addresses'>('details');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  // --- Email Handlers ---
  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, {
        id: `email-${Date.now()}`,
        type: 'Work',
        value: '',
        isPrimary: prev.emails.length === 0
      }]
    }));
  };
  const removeEmail = (id: string) => {
    setFormData(prev => {
      const newEmails = prev.emails.filter(e => e.id !== id);
      // If we removed the primary email, make the first one primary
      if (newEmails.length > 0 && !newEmails.some(e => e.isPrimary)) {
        newEmails[0].isPrimary = true;
      }
      return {
        ...prev,
        emails: newEmails
      };
    });
  };
  const updateEmail = (id: string, field: keyof ContactEmail, value: any) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map(email => {
        if (email.id !== id) return email;
        return {
          ...email,
          [field]: value
        };
      })
    }));
  };
  const setPrimaryEmail = (id: string) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map(email => ({
        ...email,
        isPrimary: email.id === id
      }))
    }));
  };
  // --- Phone Handlers ---
  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev.phones, {
        id: `phone-${Date.now()}`,
        type: 'Mobile',
        value: '',
        isPrimary: prev.phones.length === 0
      }]
    }));
  };
  const removePhone = (id: string) => {
    setFormData(prev => {
      const newPhones = prev.phones.filter(p => p.id !== id);
      if (newPhones.length > 0 && !newPhones.some(p => p.isPrimary)) {
        newPhones[0].isPrimary = true;
      }
      return {
        ...prev,
        phones: newPhones
      };
    });
  };
  const updatePhone = (id: string, field: keyof ContactPhone, value: any) => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.map(phone => {
        if (phone.id !== id) return phone;
        return {
          ...phone,
          [field]: value
        };
      })
    }));
  };
  const setPrimaryPhone = (id: string) => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.map(phone => ({
        ...phone,
        isPrimary: phone.id === id
      }))
    }));
  };
  // --- Address Handlers ---
  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, {
        id: `addr-${Date.now()}`,
        type: 'Physical',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        isPrimary: prev.addresses.length === 0
      }]
    }));
    setActiveTab('addresses');
  };
  const removeAddress = (id: string) => {
    setFormData(prev => {
      const newAddresses = prev.addresses.filter(a => a.id !== id);
      if (newAddresses.length > 0 && !newAddresses.some(a => a.isPrimary)) {
        newAddresses[0].isPrimary = true;
      }
      return {
        ...prev,
        addresses: newAddresses
      };
    });
  };
  const updateAddress = (id: string, field: keyof ContactAddress, value: any) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr => {
        if (addr.id !== id) return addr;
        return {
          ...addr,
          [field]: value
        };
      })
    }));
  };
  const setPrimaryAddress = (id: string) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr => ({
        ...addr,
        isPrimary: addr.id === id
      }))
    }));
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    // Validate emails
    if (formData.emails.length === 0) {
      newErrors.emails = 'At least one email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hasValidEmail = formData.emails.some(e => e.value.trim() && emailRegex.test(e.value));
      if (!hasValidEmail) {
        newErrors.emails = 'Please provide a valid email address';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Ensure legacy fields are populated for backward compatibility
      const primaryEmail = formData.emails.find(e => e.isPrimary) || formData.emails[0];
      const primaryPhone = formData.phones.find(p => p.isPrimary) || formData.phones[0];
      const submissionData = {
        ...formData,
        email: primaryEmail?.value || '',
        phone: primaryPhone?.value || ''
      };
      onSave(submissionData);
    }
  };
  return <div className="bg-white flex flex-col h-[90vh] sm:h-auto sm:max-h-[90vh]" data-id="element-9211">
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-9212">
        <div className="flex items-center justify-between" data-id="element-9213">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-9214">
            {contact ? 'Edit Contact' : 'Add New Contact'}
          </h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-500" data-id="element-9215">
            <XIcon className="h-5 w-5" data-id="element-9216" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mt-4 border-b border-gray-100" data-id="element-9217">
          <button type="button" onClick={() => setActiveTab('details')} className={`pb-2 text-sm font-medium ${activeTab === 'details' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-9218">
            Contact Details
          </button>
          <button type="button" onClick={() => setActiveTab('addresses')} className={`pb-2 text-sm font-medium ${activeTab === 'addresses' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-9219">
            Addresses ({formData.addresses.length})
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-9220">
        <form id="contact-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-6" data-id="element-9221">
          {activeTab === 'details' ? <>
              {/* Basic Info */}
              <div className="space-y-4" data-id="element-9222">
                <div data-id="element-9223">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-9224">
                    Name <span className="text-red-500" data-id="element-9225">*</span>
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${errors.name ? 'border-red-300' : ''}`} placeholder="Enter contact name" data-id="element-9226" />
                  {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-9227">{errors.name}</p>}
                </div>

                <div data-id="element-9228">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700" data-id="element-9229">
                    Role / Title
                  </label>
                  <input type="text" id="role" name="role" value={formData.role} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g., Site Manager, Finance Director" data-id="element-9230" />
                </div>

                <div className="flex items-center" data-id="element-9231">
                  <input type="checkbox" id="isPrimary" name="isPrimary" checked={formData.isPrimary} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9232" />
                  <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-900" data-id="element-9233">
                    Set as primary contact for organization
                  </label>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6" data-id="element-9234"></div>

              {/* Emails Section */}
              <div className="space-y-3" data-id="element-9235">
                <div className="flex items-center justify-between" data-id="element-9236">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-9237">
                    Email Addresses <span className="text-red-500" data-id="element-9238">*</span>
                  </label>
                  <button type="button" onClick={addEmail} className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800" data-id="element-9239">
                    <PlusIcon className="h-3 w-3 mr-1" data-id="element-9240" />
                    Add Email
                  </button>
                </div>

                {formData.emails.map((email, index) => <div key={email.id} className="flex items-start gap-2 bg-gray-50 p-3 rounded-md" data-id="element-9241">
                    <div className="mt-2" data-id="element-9242">
                      <MailIcon className="h-4 w-4 text-gray-400" data-id="element-9243" />
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2" data-id="element-9244">
                      <div className="sm:col-span-3" data-id="element-9245">
                        <select value={email.type} onChange={e => updateEmail(email.id, 'type', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" data-id="element-9246">
                          <option value="Work" data-id="element-9247">Work</option>
                          <option value="Personal" data-id="element-9248">Personal</option>
                          <option value="Other" data-id="element-9249">Other</option>
                        </select>
                      </div>
                      <div className="sm:col-span-9" data-id="element-9250">
                        <input type="email" value={email.value} onChange={e => updateEmail(email.id, 'value', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" placeholder="email@example.com" data-id="element-9251" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1" data-id="element-9252">
                      <button type="button" onClick={() => setPrimaryEmail(email.id)} className={`p-1 rounded-full ${email.isPrimary ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`} title={email.isPrimary ? 'Primary Email' : 'Set as Primary'} data-id="element-9253">
                        <CheckIcon className="h-4 w-4" data-id="element-9254" />
                      </button>
                      {formData.emails.length > 1 && <button type="button" onClick={() => removeEmail(email.id)} className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50" data-id="element-9255">
                          <TrashIcon className="h-4 w-4" data-id="element-9256" />
                        </button>}
                    </div>
                  </div>)}
                {errors.emails && <p className="text-sm text-red-600" data-id="element-9257">{errors.emails}</p>}
              </div>

              {/* Phones Section */}
              <div className="space-y-3 pt-2" data-id="element-9258">
                <div className="flex items-center justify-between" data-id="element-9259">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-9260">
                    Phone Numbers
                  </label>
                  <button type="button" onClick={addPhone} className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800" data-id="element-9261">
                    <PlusIcon className="h-3 w-3 mr-1" data-id="element-9262" />
                    Add Phone
                  </button>
                </div>

                {formData.phones.length === 0 && <div className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded-md text-center" data-id="element-9263">
                    No phone numbers added.
                  </div>}

                {formData.phones.map(phone => <div key={phone.id} className="flex items-start gap-2 bg-gray-50 p-3 rounded-md" data-id="element-9264">
                    <div className="mt-2" data-id="element-9265">
                      <PhoneIcon className="h-4 w-4 text-gray-400" data-id="element-9266" />
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2" data-id="element-9267">
                      <div className="sm:col-span-3" data-id="element-9268">
                        <select value={phone.type} onChange={e => updatePhone(phone.id, 'type', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" data-id="element-9269">
                          <option value="Mobile" data-id="element-9270">Mobile</option>
                          <option value="Work" data-id="element-9271">Work</option>
                          <option value="Home" data-id="element-9272">Home</option>
                          <option value="Fax" data-id="element-9273">Fax</option>
                        </select>
                      </div>
                      <div className="sm:col-span-9" data-id="element-9274">
                        <input type="tel" value={phone.value} onChange={e => updatePhone(phone.id, 'value', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" placeholder="+1 (555) 123-4567" data-id="element-9275" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1" data-id="element-9276">
                      <button type="button" onClick={() => setPrimaryPhone(phone.id)} className={`p-1 rounded-full ${phone.isPrimary ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`} title={phone.isPrimary ? 'Primary Phone' : 'Set as Primary'} data-id="element-9277">
                        <CheckIcon className="h-4 w-4" data-id="element-9278" />
                      </button>
                      <button type="button" onClick={() => removePhone(phone.id)} className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50" data-id="element-9279">
                        <TrashIcon className="h-4 w-4" data-id="element-9280" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </> /* Addresses Tab */ : <div className="space-y-4" data-id="element-9281">
              <div className="flex items-center justify-between mb-2" data-id="element-9282">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-9283">
                  Address List
                </h4>
                <button type="button" onClick={addAddress} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9284">
                  <PlusIcon className="h-3 w-3 mr-1" data-id="element-9285" />
                  Add Address
                </button>
              </div>

              {formData.addresses.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200" data-id="element-9286">
                  <MapPinIcon className="mx-auto h-8 w-8 text-gray-400" data-id="element-9287" />
                  <p className="mt-2 text-sm text-gray-500" data-id="element-9288">
                    No addresses added yet
                  </p>
                  <button type="button" onClick={addAddress} className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-9289">
                    Add one now
                  </button>
                </div> : <div className="space-y-4" data-id="element-9290">
                  {formData.addresses.map(addr => <div key={addr.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 relative" data-id="element-9291">
                      <div className="absolute top-2 right-2 flex items-center gap-1" data-id="element-9292">
                        <button type="button" onClick={() => setPrimaryAddress(addr.id)} className={`p-1 rounded-full ${addr.isPrimary ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`} title={addr.isPrimary ? 'Primary Address' : 'Set as Primary'} data-id="element-9293">
                          <CheckIcon className="h-4 w-4" data-id="element-9294" />
                        </button>
                        <button type="button" onClick={() => removeAddress(addr.id)} className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50" data-id="element-9295">
                          <TrashIcon className="h-4 w-4" data-id="element-9296" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-y-3 gap-x-4 sm:grid-cols-6" data-id="element-9297">
                        <div className="sm:col-span-2" data-id="element-9298">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9299">
                            Type
                          </label>
                          <select value={addr.type} onChange={e => updateAddress(addr.id, 'type', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-9300">
                            <option value="Physical" data-id="element-9301">Physical</option>
                            <option value="Mailing" data-id="element-9302">Mailing</option>
                            <option value="Billing" data-id="element-9303">Billing</option>
                            <option value="Shipping" data-id="element-9304">Shipping</option>
                          </select>
                        </div>

                        <div className="sm:col-span-4" data-id="element-9305">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9306">
                            Street Address
                          </label>
                          <input type="text" value={addr.street} onChange={e => updateAddress(addr.id, 'street', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="123 Main St" data-id="element-9307" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-9308">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9309">
                            City
                          </label>
                          <input type="text" value={addr.city} onChange={e => updateAddress(addr.id, 'city', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-9310" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-9311">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9312">
                            State/Province
                          </label>
                          <input type="text" value={addr.state} onChange={e => updateAddress(addr.id, 'state', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-9313" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-9314">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9315">
                            Postal Code
                          </label>
                          <input type="text" value={addr.postalCode} onChange={e => updateAddress(addr.id, 'postalCode', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-9316" />
                        </div>

                        <div className="sm:col-span-3" data-id="element-9317">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9318">
                            Country
                          </label>
                          <input type="text" value={addr.country} onChange={e => updateAddress(addr.id, 'country', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-9319" />
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>}
        </form>
      </div>

      <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0" data-id="element-9320">
        <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9321">
          Cancel
        </button>
        <button type="submit" form="contact-form" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9322">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-9323" />
          {contact ? 'Update Contact' : 'Add Contact'}
        </button>
      </div>
    </div>;
}